import { FunctionComponent, useMemo } from 'react';
import FileUploadHeader from './FileUploadHeader';
import { useTranslation } from 'react-i18next';
import { Table } from 'UI';
import { Card, formattedCurrency, Box } from 'vopay';

type FileUploadHeaderProps = {
  product: string;
  service: string;
  fileName: string;
  numberOfTransactions: number;
  amount: number;
  currency: string;
  ClientAccountsEnabled: string;
  transactionData: {
    [key: string]: string | number | boolean | undefined;
  }[];
};

type FC = FunctionComponent<FileUploadHeaderProps>;

const FileUploadReview: FC = (props: FileUploadHeaderProps) => {
  const { product, service, fileName, numberOfTransactions, amount, currency, transactionData, ClientAccountsEnabled } =
    props;
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Review' });

  const getTableProps = () => {
    if (product === 'eLinx Pay') {
      const baseColumns = [
        { id: 'Id', header: t('ELinxTable.id'), align: 'left' as const, template: (row: any) => row.data.Id },
        {
          id: 'ClientAccountID',
          header: t('ELinxTable.clientAccountID'),
          align: 'left' as const,
          template: (row: any) => row.data.ClientAccountID,
        },
        {
          id: 'Amount',
          header: t('ELinxTable.amount'),
          align: 'left' as const,
          template: (row: any) => row.data.Amount,
        },
        { id: 'Type', header: t('ELinxTable.type'), align: 'left' as const, template: (row: any) => row.data.Type },
        {
          id: 'PaymentType',
          header: t('ELinxTable.paymentType'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentType,
        },
        {
          id: 'PaymentFrequency',
          header: t('ELinxTable.paymentFrequency'),
          align: 'left' as const,
          template: (row: any) => row.data.PaymentFrequency,
        },
        {
          id: 'ScheduleStartDate',
          header: t('ELinxTable.scheduleStartDate'),
          align: 'left' as const,
          template: (row: any) => row.data.ScheduleStartDate,
        },
        {
          id: 'ScheduleEndDate',
          header: t('ELinxTable.scheduleEndDate'),
          align: 'left' as const,
          template: (row: any) => row.data.ScheduleEndDate,
        },
        {
          id: 'NameOfFrequency',
          header: t('ELinxTable.nameOfFrequency'),
          align: 'left' as const,
          template: (row: any) => row.data.NameOfFrequency,
        },
        {
          id: 'SemiMonthlyFrequencyType',
          header: t('ELinxTable.semiMonthlyFrequencyType'),
          align: 'left' as const,
          template: (row: any) => row.data.SemiMonthlyFrequencyType,
        },
        {
          id: 'EndingAfterPayments',
          header: t('ELinxTable.endingAfterPayments'),
          align: 'left' as const,
          template: (row: any) => row.data.EndingAfterPayments,
        },
        {
          id: 'MessageForRecipient',
          header: t('ELinxTable.messageForRecipient'),
          align: 'left' as const,
          template: (row: any) => row.data.MessageForRecipient,
        },
        {
          id: 'SendEmail',
          header: t('ELinxTable.sendEmail'),
          align: 'left' as const,
          template: (row: any) => row.data.SendEmail,
        },
        { id: 'Note', header: t('ELinxTable.note'), align: 'left' as const, template: (row: any) => row.data.Note },
        {
          id: 'ClientReferenceNumber',
          header: t('ELinxTable.clientReferenceNumber'),
          align: 'left' as const,
          template: (row: any) => row.data.ClientReferenceNumber,
        },
        {
          id: 'InvoiceNumber',
          header: t('ELinxTable.invoiceNumber'),
          align: 'left' as const,
          template: (row: any) => row.data.InvoiceNumber,
        },
        {
          id: 'DocumentID',
          header: t('ELinxTable.documentID'),
          align: 'left' as const,
          template: (row: any) => row.data.DocumentID,
        },
        {
          id: 'TransactionLabel',
          header: t('ELinxTable.transactionLabel'),
          align: 'left' as const,
          template: (row: any) => row.data.TransactionLabel,
        },
        {
          id: 'IframeKey',
          header: t('ELinxTable.iframeKey'),
          align: 'left' as const,
          template: (row: any) => row.data.IframeKey,
        },
      ];

      const conditionalColumns = [];
      if (ClientAccountsEnabled !== '1') {
        conditionalColumns.push(
          { id: 'Name', header: t('ELinxTable.name'), align: 'left' as const, template: (row: any) => row.data.Name },
          {
            id: 'Account',
            header: t('ELinxTable.account'),
            align: 'left' as const,
            template: (row: any) => row.data.Account,
          },
        );
      }

      return {
        id: 'bulkTransactionTable',
        version: '2' as const,
        zebra: true,
        columns: [
          baseColumns[0], // Id
          baseColumns[1], // ClientAccountID
          ...conditionalColumns, // Name and Account if enabled
          ...baseColumns.slice(2), // Rest of the columns
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transaction-${index}`,
            data: {
              Id: String(index + 1),
              ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
              Name: String(
                transaction.CompanyName
                  ? transaction.CompanyName
                  : transaction.RecipientName
                    ? transaction.RecipientName
                    : transaction.ReceiverName
                      ? transaction.ReceiverName
                      : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
              ),
              Account: (
                <>
                  {transaction.CardNumber ? transaction.CardNumber : ''}
                  {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                  {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                  {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                  {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                  {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                  {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                  {transaction.CreditCardToken
                    ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                    : ''}
                  {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                  {transaction.FlinksAccountID
                    ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                    : ''}
                  {transaction.FlinksLoginID
                    ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                    : ''}
                  {transaction.PlaidPublicToken
                    ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                    : ''}
                  {transaction.PlaidAccessToken
                    ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                    : ''}
                  {transaction.PlaidAccountID
                    ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                    : ''}
                  {transaction.PlaidProcessorToken
                    ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                    : ''}
                  {transaction.InveriteRequestGUID
                    ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                    : ''}
                </>
              ),
              Amount: `$ ${String(transaction.Amount || '')}`,
              Type: transaction.Frequency
                ? t('Table.Row.typeScheduled')
                : transaction.Amount
                  ? t('Table.Row.typeTransaction')
                  : t('Table.Row.typeBeneficiary'),
              PaymentType: transaction.PaymentType ? String(transaction.PaymentType) : '',
              PaymentFrequency: transaction.Frequency ? String(transaction.Frequency) : '',
              ScheduleStartDate: transaction.ScheduleStartDate ? String(transaction.ScheduleStartDate) : '',
              ScheduleEndDate: transaction.ScheduleEndDate ? String(transaction.ScheduleEndDate) : '',
              NameOfFrequency: transaction.NameOfFrequency ? String(transaction.NameOfFrequency) : '',
              SemiMonthlyFrequencyType: transaction.SemiMonthlyFrequencyType
                ? String(transaction.SemiMonthlyFrequencyType)
                : '',
              EndingAfterPayments: transaction.EndingAfterPayments ? String(transaction.EndingAfterPayments) : '',
              MessageForRecipient: transaction.RecipientMessage ? String(transaction.RecipientMessage) : '',
              SendEmail: transaction.SendEmail ? String(transaction.SendEmail) : '',
              Note: transaction.Note ? String(transaction.Note) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              InvoiceNumber: transaction.InvoiceNumber ? String(transaction.InvoiceNumber) : '',
              DocumentID: transaction.DocumentID ? String(transaction.DocumentID) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
              IframeKey: transaction.IframeKey ? String(transaction.IframeKey) : '',
            },
          })) || [],
      };
    } else if (product === 'Client Account Transfer') {
      return {
        id: 'bulkTransactionTable',
        version: '2' as const,
        zebra: true,
        columns: [
          {
            id: 'Id',
            header: t('ClientAccountTransferTable.id'),
            align: 'left' as const,
            template: (row: any) => row.data.Id,
          },
          {
            id: 'TransferType',
            header: t('ClientAccountTransferTable.transferType'),
            align: 'left' as const,
            template: (row: any) => row.data.TransferType,
          },
          {
            id: 'DebitorClientAccountID',
            header: t('ClientAccountTransferTable.debitorClientAccountID'),
            align: 'left' as const,
            template: (row: any) => row.data.DebitorClientAccountID,
          },
          {
            id: 'RecipientClientAccountID',
            header: t('ClientAccountTransferTable.recipientClientAccountID'),
            align: 'left' as const,
            template: (row: any) => row.data.RecipientClientAccountID,
          },
          {
            id: 'Amount',
            header: t('ClientAccountTransferTable.amount'),
            align: 'left' as const,
            template: (row: any) => row.data.Amount,
          },
          {
            id: 'FundingRail',
            header: t('ClientAccountTransferTable.fundingRail'),
            align: 'left' as const,
            template: (row: any) => row.data.FundingRail,
          },
          {
            id: 'WithdrawRail',
            header: t('ClientAccountTransferTable.withdrawRail'),
            align: 'left' as const,
            template: (row: any) => row.data.WithdrawRail,
          },
          {
            id: 'Currency',
            header: t('ClientAccountTransferTable.currency'),
            align: 'left' as const,
            template: (row: any) => row.data.Currency,
          },
          {
            id: 'ReverseOnFailure',
            header: t('ClientAccountTransferTable.reverseOnFailure'),
            align: 'left' as const,
            template: (row: any) => row.data.ReverseOnFailure,
          },
          {
            id: 'ClientReferenceNumber',
            header: t('ClientAccountTransferTable.clientReferenceNumber'),
            align: 'left' as const,
            template: (row: any) => row.data.ClientReferenceNumber,
          },
          {
            id: 'Notes',
            header: t('ClientAccountTransferTable.notes'),
            align: 'left' as const,
            template: (row: any) => row.data.Notes,
          },
          {
            id: 'TransactionLabel',
            header: t('ClientAccountTransferTable.transactionLabel'),
            align: 'left' as const,
            template: (row: any) => row.data.TransactionLabel,
          },
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transfer-${index}`,
            data: {
              Id: String(index + 1),
              TransferType: transaction.TransferType ? String(transaction.TransferType) : '',
              DebitorClientAccountID: transaction.DebitorClientAccountID
                ? String(transaction.DebitorClientAccountID)
                : '',
              RecipientClientAccountID: transaction.RecipientClientAccountID
                ? String(transaction.RecipientClientAccountID)
                : '',
              Amount: `$ ${String(transaction.Amount || '')}`,
              FundingRail: transaction.FundingRail ? String(transaction.FundingRail) : '',
              WithdrawRail: transaction.WithdrawRail ? String(transaction.WithdrawRail) : '',
              Currency: transaction.Currency ? String(transaction.Currency) : '',
              ReverseOnFailure: transaction.ReverseOnFailure !== undefined ? String(transaction.ReverseOnFailure) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              Notes: transaction.Notes ? String(transaction.Notes) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
            },
          })) || [],
      };
    }
    const defaultColumns = [
      { id: 'Id', header: t('Table.id'), align: 'left' as const, template: (row: any) => row.data.Id },
      { id: 'Amount', header: t('Table.amount'), align: 'left' as const, template: (row: any) => row.data.Amount },
      { id: 'Service', header: t('Table.service'), align: 'left' as const, template: (row: any) => row.data.Service },
    ];

    const conditionalDefaultColumns = [];
    if (product === 'eLinx Pay' || ClientAccountsEnabled === '1') {
      conditionalDefaultColumns.push({
        id: 'ClientAccountID',
        header: t('Table.clientAccountID'),
        align: 'left' as const,
        template: (row: any) => row.data.ClientAccountID,
      });
    }
    if (ClientAccountsEnabled !== '1') {
      conditionalDefaultColumns.push(
        { id: 'Name', header: t('Table.name'), align: 'left' as const, template: (row: any) => row.data.Name },
        { id: 'Account', header: t('Table.account'), align: 'left' as const, template: (row: any) => row.data.Account },
      );
    }

    return {
      id: 'bulkTransactionTable',
      version: '2' as const,
      zebra: true,
      columns: [
        defaultColumns[0], // Id
        ...conditionalDefaultColumns, // ClientAccountID, Name, Account based on conditions
        ...defaultColumns.slice(1), // Amount, Service
      ],
      rows:
        Object.values(transactionData).map((transaction, index) => ({
          id: `default-${index}`,
          data: {
            Id: String(index + 1),
            ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
            Name: String(
              transaction.CompanyName
                ? transaction.CompanyName
                : transaction.RecipientName
                  ? transaction.RecipientName
                  : transaction.ReceiverName
                    ? transaction.ReceiverName
                    : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
            ),
            Account: (
              <>
                {transaction.CardNumber ? transaction.CardNumber : ''}
                {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                {transaction.Email ? transaction.Email : ''}
                {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                {transaction.CreditCardToken
                  ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                  : ''}
                {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                {transaction.FlinksAccountID
                  ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                  : ''}
                {transaction.FlinksLoginID
                  ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                  : ''}
                {transaction.PlaidPublicToken
                  ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                  : ''}
                {transaction.PlaidAccessToken
                  ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                  : ''}
                {transaction.PlaidAccountID
                  ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                  : ''}
                {transaction.PlaidProcessorToken
                  ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                  : ''}
                {transaction.InveriteRequestGUID
                  ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                  : ''}
              </>
            ),
            Amount: `$${formattedCurrency((transaction.Amount || '').toString() || '')}`,
            Service: product === 'eLinx Pay' ? 'eLinx Pay' : service,
          },
        })) || [],
    };
  };

  const TableProps = useMemo(() => {
    return getTableProps() as any;
  }, [transactionData]);

  return (
    <div>
      <Box flx="col" pb="lg" mb="md" gap="sm" jc="sb" width="100%" wrap="wrap">
        <FileUploadHeader
          product={product}
          service={service}
          fileName={fileName}
          numberOfTransactions={numberOfTransactions}
          amount={amount}
          currency={currency}
        />
        <Card style={{ padding: '16px' }}>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table {...TableProps} />
          </div>
        </Card>
      </Box>
    </div>
  );
};

export default FileUploadReview;
